// Admin Progress Analytics Dashboard
// Aggregated cohort metrics from /api/admin/analytics/overview
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AdminCohortChallenges from "./AdminCohortChallenges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  bg: "#060606", surface: "#0C0C0C", panel: "#13161c",
  border: "#2a2e36", gold: "#C9A227", text: "#EDE8DC", mute: "#888",
  green: "#22C55E", red: "#FF4444", blue: "#3B82F6",
};

const COURSE_LABELS = {
  beginner: "Beginner",
  advanced: "Advanced",
  cfd: "CFD",
  commodities: "Commodities",
  roadmap: "Roadmap",
};

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quizStats, setQuizStats] = useState(null);
  const [quizError, setQuizError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = localStorage.getItem("flexx_access_token");
        const [overview, quiz] = await Promise.allSettled([
          axios.get(`${API}/admin/analytics/overview`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/quiz-attempts/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (!mounted) return;
        if (overview.status === "fulfilled") setData(overview.value.data);
        else setError(overview.reason?.response?.data?.detail || overview.reason?.message || "Failed to load");
        if (quiz.status === "fulfilled") setQuizStats(quiz.value.data);
        else setQuizError(quiz.reason?.response?.data?.detail || quiz.reason?.message || "Failed to load quiz stats");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) return <Wrap>Loading analytics…</Wrap>;
  if (error) return <Wrap><div style={{ color: T.red }}>{error}</div></Wrap>;
  if (!data) return null;

  const totals = data.totals || {};
  const byRole = data.by_role || {};
  const courseAccess = data.course_access || {};
  const moduleCompletion = data.module_completion || {};
  const totalAccessGrants = Object.values(courseAccess).reduce((a, b) => a + b, 0) || 1;

  return (
    <div data-testid="admin-analytics" style={{ minHeight: "100vh", background: T.bg, color: T.text, padding: "32px 20px 80px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 2.5, color: T.gold, fontFamily: "monospace" }}>FLEXX ADMIN</div>
            <h1 style={{ margin: "4px 0 0", fontSize: 32, fontWeight: 800, fontFamily: "'Bebas Neue', 'Oswald', sans-serif", letterSpacing: 0.5 }}>PROGRESS ANALYTICS</h1>
            <div style={{ color: T.mute, fontSize: 11, fontFamily: "monospace", marginTop: 6 }}>
              Generated {new Date(data.generated_at).toLocaleString()}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="admin-back-btn" onClick={() => navigate("/admin")} style={ghostBtn}>← Admin Home</button>
            <button data-testid="admin-refresh-btn" onClick={() => window.location.reload()} style={primaryBtn}>Refresh</button>
          </div>
        </div>

        {/* KPI tiles */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
          <Tile testid="stat-total-users" label="TOTAL USERS" value={totals.users ?? 0} accent={T.gold} />
          <Tile testid="stat-active-7d" label="ACTIVE · 7d" value={totals.active_7d ?? 0} sub={`${pct(totals.active_7d, totals.users)}%`} accent={T.green} />
          <Tile testid="stat-active-30d" label="ACTIVE · 30d" value={totals.active_30d ?? 0} sub={`${pct(totals.active_30d, totals.users)}%`} accent={T.blue} />
          <Tile testid="stat-inactive-30d" label="INACTIVE · 30d+" value={totals.inactive_30d ?? 0} accent={T.red} />
          <Tile testid="stat-new-7d" label="NEW · 7d" value={totals.new_7d ?? 0} sub={`+${totals.new_7d ?? 0} this week`} accent={T.gold} />
          <Tile testid="stat-new-30d" label="NEW · 30d" value={totals.new_30d ?? 0} accent={T.gold} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16, marginBottom: 24 }}>
          {/* Roles */}
          <Card testid="admin-roles-card" title="USERS BY ROLE">
            {Object.keys(byRole).length === 0 ? (
              <Empty>No role data</Empty>
            ) : (
              Object.entries(byRole)
                .sort((a, b) => b[1] - a[1])
                .map(([role, count]) => (
                  <Bar key={role} testid={`role-${role}`} label={role.toUpperCase()} value={count} max={totals.users || 1} color={T.gold} />
                ))
            )}
          </Card>

          {/* Course access distribution */}
          <Card testid="admin-courses-card" title="COURSE ACCESS DISTRIBUTION">
            {Object.entries(courseAccess).map(([course, count]) => (
              <Bar key={course} testid={`course-${course}`}
                label={COURSE_LABELS[course] || course}
                value={count}
                max={totals.users || 1}
                color={T.gold}
                note={`${count} users (${pct(count, totalAccessGrants)}% of grants)`} />
            ))}
          </Card>

          {/* Module completion sum */}
          <Card testid="admin-completion-card" title="LESSONS COMPLETED · ALL USERS">
            {Object.entries(moduleCompletion).map(([course, count]) => {
              const max = Math.max(...Object.values(moduleCompletion), 1);
              return (
                <Bar key={course} testid={`completion-${course}`}
                  label={COURSE_LABELS[course] || course} value={count} max={max} color={T.green}
                  note={`${count} lessons completed`} />
              );
            })}
          </Card>
        </div>

        {/* Recent signups + top performers */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16 }}>
          <Card testid="admin-recent-card" title="RECENT SIGNUPS · LAST 10">
            {(data.recent_signups || []).length === 0 ? (
              <Empty>No recent signups</Empty>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {(data.recent_signups || []).map((u, i) => (
                  <div key={i} data-testid={`recent-signup-${i}`} style={rowStyle}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ color: T.text, fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</div>
                      <div style={{ color: T.mute, fontSize: 11, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: T.gold, fontSize: 10, fontFamily: "monospace", letterSpacing: 1 }}>{u.role.toUpperCase()}</div>
                      <div style={{ color: T.mute, fontSize: 10, fontFamily: "monospace" }}>{shortDate(u.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card testid="admin-top-performers-card" title="TOP PERFORMERS · TOTAL LESSONS COMPLETED">
            {(data.top_performers || []).length === 0 ? (
              <Empty>No completion data yet</Empty>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {(data.top_performers || []).map((u, i) => (
                  <div key={i} data-testid={`top-performer-${i}`} style={rowStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center",
                        background: i === 0 ? T.gold : T.border,
                        color: i === 0 ? "#0a0a0a" : T.text,
                        fontFamily: "monospace", fontSize: 12, fontWeight: 800, flexShrink: 0,
                      }}>{i + 1}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: T.text, fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</div>
                        <div style={{ color: T.mute, fontSize: 11, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</div>
                      </div>
                    </div>
                    <div style={{ color: T.gold, fontSize: 18, fontWeight: 800, fontFamily: "monospace" }}>
                      {u.completed_total}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* AXEL Nudge Effectiveness — proves the proactive coach loop works */}
        <div style={{ marginTop: 16 }}>
          <AxelNudgeAnalyticsCard />
        </div>

        {/* Quiz Insights — pass-rate per lesson, lowest first */}
        <div style={{ marginTop: 16 }}>
          <QuizInsightsCard stats={quizStats} error={quizError} />
        </div>

        {/* Cohort Challenges — admin-defined themed XP sprints */}
        <div style={{ marginTop: 16 }}>
          <AdminCohortChallenges />
        </div>
      </div>
    </div>
  );
}

function QuizInsightsCard({ stats, error }) {
  const items = (stats?.items || []).slice().sort((a, b) => {
    // Lowest pass_rate first; ties broken by most attempts
    if ((a.pass_rate ?? 0) !== (b.pass_rate ?? 0)) return (a.pass_rate ?? 0) - (b.pass_rate ?? 0);
    return (b.total_attempts ?? 0) - (a.total_attempts ?? 0);
  });
  return (
    <div data-testid="admin-quiz-insights-card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12, flexWrap: "wrap", gap: 6 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: T.gold, fontFamily: "monospace" }}>QUIZ INSIGHTS · LOWEST PASS-RATE FIRST</div>
        <div style={{ color: T.mute, fontSize: 11, fontFamily: "monospace" }}>
          {items.length > 0 ? `${items.length} lesson${items.length === 1 ? "" : "s"}` : ""}
        </div>
      </div>
      {error ? (
        <Empty>Couldn't load quiz stats: {String(error)}</Empty>
      ) : items.length === 0 ? (
        <Empty>No quiz attempts logged yet.</Empty>
      ) : (
        <div data-testid="admin-quiz-insights-table-wrap" style={{ overflowX: "auto" }}>
          <table data-testid="admin-quiz-insights-table" style={{
            width: "100%", borderCollapse: "collapse", minWidth: 560, fontFamily: "monospace", fontSize: 12,
          }}>
            <thead>
              <tr style={{ background: T.panel, color: T.mute, fontSize: 10, letterSpacing: 1.5 }}>
                <th style={thStyle}>LESSON ID</th>
                <th style={thStyle}>ATTEMPTS</th>
                <th style={thStyle}>UNIQUE USERS</th>
                <th style={thStyle}>PASS RATE</th>
                <th style={thStyle}>AVG SCORE</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const passRatePct = Math.round((r.pass_rate ?? 0) * 100);
                const passColor = passRatePct >= 75 ? T.green : passRatePct >= 50 ? T.gold : T.red;
                return (
                  <tr key={r.lesson_id} data-testid={`quiz-row-${r.lesson_id}`} style={{ borderTop: `1px solid ${T.border}` }}>
                    <td style={{ ...tdStyle, color: T.text, fontWeight: 700 }}>{r.lesson_id || "—"}</td>
                    <td style={{ ...tdStyle, color: T.text }}>{r.total_attempts ?? 0}</td>
                    <td style={{ ...tdStyle, color: T.text }}>{r.unique_users ?? 0}</td>
                    <td style={{ ...tdStyle, color: passColor, fontWeight: 800 }} data-testid={`quiz-row-${r.lesson_id}-pass-rate`}>
                      {passRatePct}%
                    </td>
                    <td style={{ ...tdStyle, color: T.text }}>{Math.round((r.avg_score ?? 0))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// AXEL nudge analytics — surfaces whether the proactive D/F push loop
// actually changes trader behavior.
function AxelNudgeAnalyticsCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/axel-nudges/analytics?days=30`, { withCredentials: true });
        if (mounted) setData(res.data);
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const totals = data?.totals || {};
  const recoveryPct = data?.recovery_pct_within_7d;
  const byPair = data?.by_pair || {};
  const daily = data?.daily || [];
  const maxDaily = daily.reduce((m, d) => Math.max(m, d.count || 0), 1);

  return (
    <div
      data-testid="admin-axel-nudges-card"
      style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16 }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12, flexWrap: "wrap", gap: 6 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: T.gold, fontFamily: "monospace" }}>
          AXEL NUDGE EFFECTIVENESS · LAST 30 DAYS
        </div>
        <div style={{ color: T.mute, fontSize: 11, fontFamily: "monospace" }}>
          The coach who notices, not the coach you have to ask.
        </div>
      </div>

      {loading && <Empty>Loading nudge analytics…</Empty>}
      {error && <Empty>Couldn't load: {String(error)}</Empty>}

      {!loading && !error && data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
            <Tile
              testid="nudge-recovery-pct"
              label="RECOVERED · A WITHIN 7d"
              value={recoveryPct == null ? "—" : `${recoveryPct}%`}
              sub={`${totals.recovered_nudges || 0} of ${totals.scored_nudges || 0} scored`}
              accent={recoveryPct == null ? T.mute : (recoveryPct >= 30 ? T.green : (recoveryPct >= 15 ? T.gold : T.red))}
            />
            <Tile testid="nudge-total" label="NUDGES FIRED" value={totals.nudges ?? 0} accent={T.gold} />
            <Tile testid="nudge-d-grade" label="D-GRADE" value={totals.d_grade ?? 0} accent={T.gold} />
            <Tile testid="nudge-f-grade" label="F-GRADE" value={totals.f_grade ?? 0} accent={T.red} />
            <Tile testid="nudge-active-subs" label="PUSH SUBS" value={totals.active_push_subs ?? 0} accent={T.blue} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
            <div data-testid="nudge-by-pair" style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: T.mute, fontFamily: "monospace", marginBottom: 10 }}>
                BY ASSET CLASS
              </div>
              {Object.keys(byPair).length === 0 ? (
                <Empty>No nudges yet — log a D/F trade to see this populate.</Empty>
              ) : (
                Object.entries(byPair).map(([k, v]) => (
                  <Bar key={k} testid={`nudge-pair-${k}`} label={k} value={v} max={totals.nudges || 1} color={T.gold} />
                ))
              )}
            </div>

            <div data-testid="nudge-daily-timeline" style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: T.mute, fontFamily: "monospace", marginBottom: 10 }}>
                DAILY TIMELINE
              </div>
              {daily.length === 0 ? (
                <Empty>No nudges in window.</Empty>
              ) : (
                <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 64 }}>
                  {daily.map((d) => (
                    <div
                      key={d.date}
                      title={`${d.date}: ${d.count}`}
                      style={{
                        flex: 1, minWidth: 4,
                        height: `${(d.count / maxDaily) * 100}%`,
                        background: T.gold,
                        borderRadius: "2px 2px 0 0",
                        opacity: 0.8,
                      }}
                    />
                  ))}
                </div>
              )}
              <div style={{ color: T.mute, fontSize: 10, fontFamily: "monospace", marginTop: 8 }}>
                {daily.length > 0 && `${daily[0].date} → ${daily[daily.length - 1].date}`}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, padding: 10, background: T.bg, border: `1px dashed ${T.border}`, borderRadius: 6, color: T.mute, fontSize: 11, fontFamily: "monospace", lineHeight: 1.5 }}>
            <b style={{ color: T.text }}>How recovery is measured:</b> a nudge counts as "recovered" when the same trader logs an
            A-grade trade on the same asset class (Gold or BTC) within 7 days of the nudge firing.
            Only nudges with a fully-elapsed 7-day window are scored — newer ones aren't dragged into the average.
            {recoveryPct != null && recoveryPct >= 30 && (
              <span style={{ color: T.green, display: "block", marginTop: 6 }}>
                ✓ A {recoveryPct}% recovery rate is investor-deck material — proactive coaching is working.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// ── helpers ──────────────────────────────────────────────────
function Wrap({ children }) {
  return <div style={{ minHeight: "100vh", background: T.bg, color: T.text, padding: 32, fontFamily: "monospace" }}>{children}</div>;
}
function Tile({ testid, label, value, sub, accent }) {
  return (
    <div data-testid={testid} style={{
      background: T.surface, border: `1px solid ${T.border}`, borderLeft: `3px solid ${accent}`,
      borderRadius: 10, padding: 14,
    }}>
      <div style={{ fontSize: 10, letterSpacing: 1.5, color: T.mute, fontFamily: "monospace" }}>{label}</div>
      <div style={{ color: T.text, fontSize: 28, fontWeight: 800, marginTop: 6, fontFamily: "'Bebas Neue','Oswald',sans-serif", letterSpacing: 0.5 }}>{value}</div>
      {sub && <div style={{ color: accent, fontSize: 11, fontFamily: "monospace", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function Card({ testid, title, children }) {
  return (
    <div data-testid={testid} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 10, letterSpacing: 2, color: T.gold, fontFamily: "monospace", marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}
function Bar({ testid, label, value, max, color, note }) {
  const pctVal = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div data-testid={testid} style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4, gap: 8 }}>
        <div style={{ color: T.text, fontSize: 12, fontFamily: "monospace", letterSpacing: 1 }}>{label}</div>
        <div style={{ color: T.mute, fontSize: 11, fontFamily: "monospace" }}>{note || `${value}`}</div>
      </div>
      <div style={{ height: 8, background: T.border, borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${pctVal}%`, height: "100%", background: color, transition: "width 0.4s" }} />
      </div>
    </div>
  );
}
function Empty({ children }) {
  return <div style={{ color: T.mute, fontSize: 12, padding: 12, textAlign: "center", fontStyle: "italic" }}>{children}</div>;
}
function pct(n, d) { return d ? Math.round((n / d) * 100) : 0; }
function shortDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch (_) { return iso; }
}

const rowStyle = {
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
  padding: "10px 12px", background: T.panel, border: `1px solid ${T.border}`, borderRadius: 8,
};
const primaryBtn = {
  padding: "8px 16px", borderRadius: 6,
  background: `linear-gradient(135deg, ${T.gold}, #E8B94F)`,
  color: "#0a0a0a", border: "none", cursor: "pointer",
  fontWeight: 800, fontFamily: "monospace", letterSpacing: 1.2, fontSize: 11, textTransform: "uppercase",
};
const ghostBtn = {
  padding: "8px 16px", borderRadius: 6,
  background: "transparent", border: `1px solid ${T.border}`, color: T.mute,
  cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: 1,
};
const thStyle = {
  textAlign: "left", padding: "10px 12px", fontWeight: 700, letterSpacing: 1.2,
  borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap",
};
const tdStyle = {
  textAlign: "left", padding: "10px 12px", whiteSpace: "nowrap",
};
